import {
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { forkJoin, Observable, of, switchMap } from 'rxjs';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import {
  BoardCard,
  BoardColumn,
  BoardContact,
  BoardField,
  BoardManager,
  CardCompanyInput,
  CardContactInput,
  CreateCardPayload,
} from '../board-v3.types';
import { BoardService } from '../../../services/board.service';
import { BaseService } from 'src/app/services/base.service';
import { environment } from '../../../../environments/environment';
import { NotificationService } from '../../../services/notifications.service';
import { TranslateService } from '@ngx-translate/core';
import { customFieldPriceValidator, getCustomFieldValidators, normalizePhonePayloadValue } from '../custom-field.validators';
import { CurrencyOption, getCurrencyOptions } from '../price-field.utils';
import { formatFullName } from '../../../helpers';
import { DialogService } from '../../../services/dialog.service';
import { QUICK_CREATE_CLIENTS_KEY } from '../sidebars/quick-create-settings/quick-create-settings.component';
import { getContactErrorKey, getContactName, getDuplicateContact } from '../contacts/contact.utils';
import {
  ContactPickerSlot,
  createEmptyContactSlot,
  getContactDraftPayload,
  getContactUpdatePayload,
} from '../contacts/contact-picker/contact-picker.component';
import { BoardContactService } from '../../../services/board-contact.service';
import { getCompanyErrorKey } from '../companies/company.utils';
import {
  CompanyPickerSlot,
  createEmptyCompanySlot,
  getCompanyDraftPayload,
} from '../companies/company-picker/company-picker.component';
import { BoardCompanyService } from '../../../services/board-company.service';
import { AddressInput } from '../../../interfaces/card';

const KNOWN_KEYS = new Set(['price', 'phones', 'emails', 'addresses', 'assigned_id', 'name', QUICK_CREATE_CLIENTS_KEY]);

interface QuickCreateApiError {
  error?: {
    errors?: { code?: string }[];
  };
}

@Component({
  selector: 'app-board-v3-quick-create-card',
  templateUrl: './quick-create-card.component.html',
  styleUrls: ['./quick-create-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class QuickCreateCardComponent implements OnChanges {
  @Input({ required: true }) column!: BoardColumn;
  @Input() funnelId: string | null = null;
  @Input() defaultCurrency: string = 'UAH';
  @Input() managers: BoardManager[] = [];
  @Input() createFields: string[] = ['phones', 'assigned_id', 'price'];
  @Input() fieldDefs: BoardField[] = [];
  @Input() compact = false;

  @Output() closeChange = new EventEmitter<void>();
  @Output() created = new EventEmitter<BoardCard>();
  @Output() settingsClick = new EventEmitter<void>();

  private readonly boardService = inject(BoardService);
  private readonly baseService = inject(BaseService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly notificationService = inject(NotificationService);
  private readonly translateService = inject(TranslateService);
  private readonly dialogService = inject(DialogService);
  private readonly contactService = inject(BoardContactService);
  private readonly companyService = inject(BoardCompanyService);

  readonly currencyOptions: CurrencyOption[] = getCurrencyOptions();
  loading = false;

  form = new FormGroup({
    name: new FormControl('', [Validators.required, Validators.maxLength(255)]),
    price: new FormControl<number | null>(null, [customFieldPriceValidator]),
    currency: new FormControl('UAH', [Validators.required]),
    assigned_id: new FormControl<string | null>(null),
    phones: new FormArray([new FormControl('')]),
    emails: new FormArray([new FormControl('')]),
    addresses: new FormControl<AddressInput[]>([], { nonNullable: true }),
    customFields: new FormGroup({}),
  });

  contactSlots: ContactPickerSlot[] = [createEmptyContactSlot()];
  companySlot: CompanyPickerSlot = createEmptyCompanySlot();
  customFieldDefs: BoardField[] = [];
  customControls: FormControl[] = [];
  customDateTimeControls: (FormControl<string> | null)[] = [];
  readonly controlSize = 'small' as const;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['column']?.currentValue) {
      this.form.reset({
        name: '',
        price: null,
        currency: this.defaultCurrency || 'UAH',
        assigned_id: this.baseService.user?.id ?? null,
        addresses: [],
      });
      while (this.phones.length > 1) this.phones.removeAt(1);
      this.phones.at(0).setValue('');
      while (this.emails.length > 1) this.emails.removeAt(1);
      this.emails.at(0).setValue('');
      this.contactSlots = [createEmptyContactSlot()];
      this.companySlot = createEmptyCompanySlot();
    }

    if (changes['createFields'] || changes['fieldDefs']) {
      this.rebuildCustomFields();
    }
  }

  private rebuildCustomFields(): void {
    const group = this.form.get('customFields') as FormGroup;
    Object.keys(group.controls).forEach((k) => group.removeControl(k));

    const requiredFields = this.fieldDefs.filter((field) => field.is_required && field.input_type !== 'checkbox');
    const fieldKeys = [
      ...this.createFields,
      ...requiredFields.map((field) => String(field.identifier ?? field.id ?? '')),
    ];
    this.customFieldDefs = [...new Set(fieldKeys)]
      .filter((k) => !KNOWN_KEYS.has(k))
      .map(
        (k) =>
          this.fieldDefs.find((f) => String(f.identifier ?? f.id ?? '') === k) ??
          ({ identifier: k, name: k } as BoardField),
      );

    this.customDateTimeControls = [];
    this.customControls = this.customFieldDefs.map((f) => {
      const key = String(f.identifier ?? f.id ?? '');
      const isCheckbox = f.input_type === 'checkbox';
      const dateParts = f.input_type === 'date' ? this.getDateParts(f.default_value) : null;
      const initialValue = isCheckbox ? Boolean(f.default_value) : (dateParts?.date ?? f.default_value ?? null);
      const validators = getCustomFieldValidators(f.input_type, f.is_required);
      const ctrl = new FormControl(initialValue, validators);
      group.addControl(key, ctrl);
      this.customDateTimeControls.push(
        f.input_type === 'date' ? new FormControl(dateParts?.time ?? '', { nonNullable: true }) : null,
      );
      return ctrl;
    });
  }

  readonly clientsKey = QUICK_CREATE_CLIENTS_KEY;

  has(key: string): boolean {
    return this.createFields.includes(key);
  }

  getDateTimeControl(index: number): FormControl<string> | null {
    return this.customDateTimeControls[index] ?? null;
  }

  fieldKey(f: BoardField): string {
    return String(f.identifier ?? f.id ?? '');
  }

  get phones(): FormArray<FormControl<string | null>> {
    return this.form.get('phones') as FormArray<FormControl<string | null>>;
  }

  get emails(): FormArray<FormControl<string | null>> {
    return this.form.get('emails') as FormArray<FormControl<string | null>>;
  }

  addPhone(): void {
    this.phones.push(new FormControl(''));
  }

  removePhone(index: number): void {
    this.phones.removeAt(index);
  }

  addEmail(): void {
    this.emails.push(new FormControl(''));
  }

  removeEmail(index: number): void {
    this.emails.removeAt(index);
  }

  get managersOptions(): { id: string; name: string; avatarUrl?: string }[] {
    return this.managers.map((m) => ({
      id: m.id,
      name: formatFullName(m.first_name, m.last_name),
      avatarUrl: m.avatar_url || undefined,
    }));
  }

  getAvatarUrl(manager: BoardManager | null): string | undefined {
    return manager?.avatar_url || undefined;
  }

  getManagerName(manager: BoardManager | null): string {
    if (!manager) return '—';
    return formatFullName(manager.first_name, manager.last_name);
  }

  close(): void {
    this.closeChange.emit();
  }

  get pickedContactIds(): string[] {
    return this.contactSlots.map((slot) => slot.existing?.id).filter((id): id is string => !!id);
  }

  addContactSlot(): void {
    this.contactSlots = [...this.contactSlots, createEmptyContactSlot()];
  }

  setContactSlot(index: number, slot: ContactPickerSlot): void {
    this.contactSlots = this.contactSlots.map((item, itemIndex) => (itemIndex === index ? slot : item));
  }

  removeContactSlot(index: number): void {
    this.contactSlots = this.contactSlots.filter((_, itemIndex) => itemIndex !== index);
    if (!this.contactSlots.length) this.contactSlots = [createEmptyContactSlot()];
  }

  submit(): void {
    this.form.markAllAsTouched();
    const contactSlots = this.has(QUICK_CREATE_CLIENTS_KEY) ? this.contactSlots : [];
    contactSlots.forEach((slot) => slot.draft?.markAllAsTouched());
    const contactsInvalid = contactSlots.some((slot) => slot.draft?.invalid);
    const companyDraft = this.has(QUICK_CREATE_CLIENTS_KEY) ? this.companySlot.draft : null;
    companyDraft?.markAllAsTouched();
    if (this.form.invalid || contactsInvalid || companyDraft?.invalid || !this.column || !this.funnelId) return;

    const v = this.form.getRawValue();
    const phones = (v.phones ?? []).map((p) => p?.trim() ?? '').filter(Boolean);
    const emails = (v.emails ?? []).map((e) => e?.trim() ?? '').filter(Boolean);

    const rawCustom = (v.customFields ?? {}) as Record<string, unknown>;
    const custom_fields = this.getCustomFieldsPayload(rawCustom);

    const payload: CreateCardPayload = {
      funnel_id: this.funnelId,
      column_id: this.column.id,
      name: v.name!,
      price: this.has('price') ? (v.price ?? undefined) : undefined,
      currency: v.currency ?? 'UAH',
      assigned_id: this.has('assigned_id') ? (v.assigned_id ?? null) : undefined,
      phones: this.has('phones') && phones.length ? phones : undefined,
      emails: this.has('emails') && emails.length ? emails : undefined,
      addresses: this.has('addresses') && v.addresses.length ? v.addresses : undefined,
      custom_fields: Object.keys(custom_fields).length ? custom_fields : undefined,
    };

    this.createCard(payload);
  }

  private createCard(payload: CreateCardPayload): void {
    const contacts = this.getContactsPayload();
    const company = this.getCompanyPayload();
    this.loading = true;
    forkJoin([this.saveEditedContacts(), this.saveEditedCompany()])
      .pipe(
        switchMap(() =>
          this.boardService.createCard({
            ...payload,
            contacts: contacts.length ? contacts : undefined,
            company: company ?? undefined,
          }),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (card) => {
          this.loading = false;
          this.created.emit(card);
          this.close();
        },
        error: (error) => {
          this.loading = false;
          const duplicate = getDuplicateContact(error);
          if (duplicate) {
            this.offerDuplicateContact(duplicate, payload);
            return;
          }
          this.showApiError(error);
        },
      });
  }

  /** Picked existing contacts whose data was edited are saved before the card links them */
  private saveEditedContacts(): Observable<unknown> {
    if (!this.has(QUICK_CREATE_CLIENTS_KEY)) return of(null);

    const updates = this.contactSlots
      .filter((slot) => slot.existing && slot.draft?.dirty)
      .map((slot) => this.contactService.updateContact(slot.existing!.id, getContactUpdatePayload(slot.draft!)));

    return updates.length ? forkJoin(updates) : of(null);
  }

  private getContactsPayload(): CardContactInput[] {
    if (!this.has(QUICK_CREATE_CLIENTS_KEY)) return [];

    return this.contactSlots
      .map((slot) => (slot.existing ? { id: slot.existing.id } : slot.draft ? getContactDraftPayload(slot.draft) : null))
      .filter((item): item is CardContactInput => !!item);
  }

  /** The picked existing company is saved before the card links it when its data was edited */
  private saveEditedCompany(): Observable<unknown> {
    const { existing, draft } = this.companySlot;
    if (!this.has(QUICK_CREATE_CLIENTS_KEY) || !existing || !draft?.dirty) return of(null);

    return this.companyService.updateCompany(existing.id, getCompanyDraftPayload(draft));
  }

  private getCompanyPayload(): CardCompanyInput | null {
    if (!this.has(QUICK_CREATE_CLIENTS_KEY)) return null;

    const { existing, draft } = this.companySlot;
    if (existing) return { id: existing.id };
    return draft ? getCompanyDraftPayload(draft) : null;
  }

  /** A new contact repeats a phone or an email of an existing one: offer to link that one and retry */
  private offerDuplicateContact(duplicate: BoardContact, payload: CreateCardPayload): void {
    const index = this.findDraftIndexFor(duplicate);
    if (index === -1) {
      const message = this.translateService.instant('board_v3_contact_duplicate', { name: getContactName(duplicate) });
      this.notificationService.showError('', String(message));
      return;
    }

    this.dialogService
      .openConfirm({
        text: this.translateService.instant('board_v3_contact_duplicate_link_confirm', {
          name: getContactName(duplicate),
        }),
        isTranslate: true,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((confirmed) => {
        if (!confirmed) return;

        if (this.pickedContactIds.includes(duplicate.id)) this.removeContactSlot(index);
        else this.setContactSlot(index, { existing: duplicate, draft: null });
        this.createCard(payload);
      });
  }

  private findDraftIndexFor(duplicate: BoardContact): number {
    const phones = new Set((duplicate.phones ?? []).map((phone) => normalizePhonePayloadValue(phone.number_e164)));
    const emails = new Set((duplicate.emails ?? []).map((email) => email.email.toLowerCase()));
    const drafts = this.contactSlots.filter((slot) => slot.draft && !slot.existing).length;

    return this.contactSlots.findIndex((slot) => {
      if (!slot.draft || slot.existing) return false;
      if (drafts === 1) return true;

      const draft = getContactDraftPayload(slot.draft);
      return (
        (draft.phones ?? []).some((phone) => phones.has(normalizePhonePayloadValue(phone))) ||
        (draft.emails ?? []).some((email) => emails.has(email.toLowerCase()))
      );
    });
  }

  private showApiError(error: unknown): void {
    const apiError = error as QuickCreateApiError;
    const code = apiError.error?.errors?.[0]?.code;
    const key =
      code === 'CUSTOM_FIELD_VALUE_INVALID'
        ? 'errors.custom_field_value_invalid'
        : code === 'COMPANY_V2_NOT_FOUND'
          ? getCompanyErrorKey(error)
          : getContactErrorKey(error);
    this.notificationService.showError('', String(this.translateService.instant(key)));
  }

  private getCustomFieldsPayload(rawCustom: Record<string, unknown>): Record<string, unknown> {
    const customFields: Record<string, unknown> = {};

    this.customFieldDefs.forEach((field, index) => {
      const key = this.fieldKey(field);
      const value = rawCustom[key];
      if (field.input_type === 'date') {
        const timeControl = this.getDateTimeControl(index);
        if (field.default_value === 'today' && !this.customControls[index].dirty && !timeControl?.dirty) return;
        const normalized = this.normalizeDateValue(value, timeControl?.value ?? '');
        if (normalized) customFields[key] = normalized;
      } else if (value !== null && value !== '' && value !== undefined) {
        customFields[key] = value;
      }
    });

    return customFields;
  }

  private getDateParts(value: unknown): { date: string | null; time: string } {
    if (value === 'today') return { date: this.localToday(), time: '' };
    if (typeof value !== 'string' || !value) return { date: null, time: '' };
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return { date: value, time: '' };

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return { date: null, time: '' };
    return {
      date: this.toLocalDate(date),
      time: `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`,
    };
  }

  private normalizeDateValue(dateValue: unknown, timeValue: string): string | null {
    if (typeof dateValue !== 'string' || !dateValue) return null;
    if (!timeValue) return dateValue;
    const [year, month, day] = dateValue.split('-').map(Number);
    const [hours, minutes] = timeValue.split(':').map(Number);
    return new Date(year, month - 1, day, hours, minutes).toISOString();
  }

  private localToday(): string {
    return this.toLocalDate(new Date());
  }

  private toLocalDate(date: Date): string {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  }
}
